import React, { useEffect } from 'react'
import { Modal } from '@/components/Modal'
import { Button, IButtonProps } from '@/components/Buttons/Button'
import { useTranslation } from '@/localization/useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { ILinkingFailure, LINKING_REASONS, isKnownLinkingReason } from '@/utils/activation'
import { clarity, CLARITY_EVENTS, CLARITY_TAGS } from '@/utils/clarity'

import classes from '../authFeedbackModal.module.scss'

/** Which handler prop an entry's extra exit maps to at render time. */
type ActivationExitAction = 'recoverAccess' | 'viewPlans'

interface IActivationExit {
  text: string
  variant: IButtonProps['variant']
  action: ActivationExitAction
}

interface IActivationEntry {
  title: string
  body: string
  /** Optional reason-specific exit, rendered before the shared Continue. */
  exit?: IActivationExit
}

// Static copy tables, module-level on purpose: nothing here closes over
// props, so nothing needs rebuilding per render. Every entry also gets the
// shared Continue button appended at render time — one definition, no drift.
const GENERIC_ENTRY: IActivationEntry = {
  title: 'We couldn\'t activate your subscription',
  body: 'We couldn\'t link your Entel activation to this account. Please open the activation link from Entel again, or contact Entel support if the problem continues.',
}

const REASON_ENTRIES: Partial<Record<string, IActivationEntry>> = {
  [LINKING_REASONS.ALREADY_CLAIMED]: {
    title: 'Your activation link was already used',
    body: 'This Entel activation was already redeemed on another account. If that account is yours, log in with it — you can recover its password if you need to.',
    exit: { text: 'Recover that account', variant: 'tertiary', action: 'recoverAccess' },
  },
  [LINKING_REASONS.TERMINATED]: {
    title: 'Your Entel subscription is no longer active',
    body: 'Entel reports this subscription as canceled, so the link can no longer be activated. You can still subscribe directly with us.',
    exit: { text: 'View plans', variant: 'tertiary', action: 'viewPlans' },
  },
  [LINKING_REASONS.LINKING_UNAVAILABLE]: {
    title: 'We couldn\'t activate your subscription yet',
    body: 'A temporary problem kept us from linking your Entel subscription. Your activation is saved — we\'ll retry automatically the next time you log in.',
  },
}

interface ActivationErrorModalProps {
  /** The failure to render — the modal is only mounted while one is pending. */
  failure: ILinkingFailure
  /** Disables every button — e.g. while the recover-access logout is in flight. */
  isBusy?: boolean
  /** Dismiss: consumes + clears the failure so the post-auth redirect resumes. */
  onContinue: () => void
  /** TERMINATED exit — AuthActor owns the destination route. */
  onViewPlans: () => void
  /**
   * ALREADY_CLAIMED exit: the subscription lives on a *different* account, so
   * AuthActor signs the live session out (if any) and lands on the
   * password-recovery form.
   */
  onRecoverAccess: () => void
}

/**
 * Auth succeeded but the activation linking did not (PRO-15): the user Entel
 * is billing has an account with no subscription attached. One message and
 * one actionable exit per `orderLinkedReason`; anything unrecognized (the
 * token list can grow backend-first) or absent falls back to the generic
 * entry. The raw reason token is telemetry-only — never rendered.
 */
export const ActivationErrorModal: React.FC<ActivationErrorModalProps> = (props) => {
  const { failure, isBusy = false, onContinue, onViewPlans, onRecoverAccess } = props
  const { t } = useTranslation()

  useEffect(() => {
    // Bucketed like the Sentry tag in withEntitlement: the reason is
    // backend-controlled, and an unrecognized-but-well-formed token must not
    // mint unbounded tag values in the Clarity dashboard.
    clarity.event(CLARITY_EVENTS.ACTIVATION_ERROR_SHOWN)
    clarity.set(
      CLARITY_TAGS.ACTIVATION_ERROR_REASON,
      isKnownLinkingReason(failure.reason) ? failure.reason : 'unknown'
    )
  }, [failure.reason])

  const exitHandlers: Record<ActivationExitAction, () => void> = {
    recoverAccess: onRecoverAccess,
    viewPlans: onViewPlans,
  }

  const entry = (failure.reason && REASON_ENTRIES[failure.reason]) || GENERIC_ENTRY

  return (
    <Modal isVisible size='medium'>
      <div className={classes.content} data-testid="activation-error-modal">
        <FontAwesomeIcon
          size="4x"
          icon={faExclamationCircle}
          color="var(--error-300)"
        />
        <div className={classes.text}>
          <h3>{t(entry.title)}</h3>
          <p>{t(entry.body)}</p>
        </div>
        <div className={classes.buttons}>
          {entry.exit && (
            <Button
              variant={entry.exit.variant}
              onClick={exitHandlers[entry.exit.action]}
              disabled={isBusy}
              fullWidth
            >
              {t(entry.exit.text)}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={onContinue}
            disabled={isBusy}
            fullWidth
          >
            {t('Continue')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
